/**
 * NOXCULT — Drops Hall v5
 * drops-hall.js
 */
(function () {
  'use strict';

  class Hall {
    constructor(el) {
      this.el       = el;
      this.items    = el.querySelectorAll('.nox-drop-item');
      this.links    = el.querySelectorAll('.nox-drop-item__link, .nox-hall__mobile-link');
      this.cssLight = document.getElementById('nox-hall-light');
      this.audioBtn = document.getElementById('nox-audio-toggle');

      const cfg        = window.__NOX__?.hall || {};
      this.intensity   = cfg.effectIntensity ?? 0.5;
      this.revealDelay = cfg.revealDelay ?? 300;
      this.transition  = cfg.transition || {};
      this.isNavigating = false;
      this._applyThemeFontVars();

      /* WebGL — NoxAtmosphere instantiated here; canvas will resize itself via ResizeObserver */
      this.atm = null;
      if ((cfg.enableWebGL ?? true) && window.NoxAtmosphere) {
        const canvas = document.getElementById('nox-hall-webgl');
        if (canvas) {
          this.atm = new NoxAtmosphere(canvas, { intensity: this.intensity, fps: 28 });
        }
      }

      /* Audio */
      this.audio = null;
      if (cfg.audio && window.NoxAudio) {
        const a = cfg.audio;
        if (a.startMuted) sessionStorage.setItem('nox_audio_muted', '1');
        this.audio = new NoxAudio({
          autoplay: a.autoplay !== false,
          ambient:  a.ambient?.src ? a.ambient : null,
          sfx: {
            navigate: a.sfx?.navigate?.src ? a.sfx.navigate : null,
            hover:    a.sfx?.hover?.src    ? a.sfx.hover    : null,
          }
        });
        this.audio.playAmbient(); // queues until first gesture
      }

      el.style.setProperty('--delay', `${this.revealDelay}ms`);
      this._bindItems();
      this._bindLinks();
      this._bindAudioBtn();
      this._reveal();
    }

    _applyThemeFontVars() {
      if (!this.el || !document.body) return;
      const bodyStyle = getComputedStyle(document.body);
      const bodyFamily = bodyStyle.fontFamily;

      const probe = document.createElement('h1');
      probe.textContent = 'A';
      probe.style.cssText = 'position:fixed;left:-9999px;top:-9999px;visibility:hidden;pointer-events:none;margin:0;padding:0;';
      document.body.appendChild(probe);
      const headingFamily = getComputedStyle(probe).fontFamily;
      probe.remove();

      if (bodyFamily) this.el.style.setProperty('--nox-theme-body-family', bodyFamily);
      if (headingFamily) this.el.style.setProperty('--nox-theme-heading-family', headingFamily);
    }

    _reveal() { setTimeout(() => this.el.classList.add('is-ready'), this.revealDelay); }

    _bindItems() {
      this.items.forEach(item => {
        const link = item.querySelector('.nox-drop-item__link');
        if (!link) return;

        link.addEventListener('mouseenter', () => {
          if (this.isNavigating) return;
          this.el.setAttribute('data-hovered', '');
          this.audio?.playSFX('hover');
          if (this.atm) {
            const rect     = item.getBoundingClientRect();
            const hallRect = this.el.getBoundingClientRect();
            const yPct     = (rect.top + rect.height / 2 - hallRect.top) / hallRect.height;
            this.atm.setFocus(0.68, yPct);
            this.atm.setMode('hover');
          } else if (this.cssLight) {
            const rect     = item.getBoundingClientRect();
            const hallRect = this.el.getBoundingClientRect();
            const yPct     = Math.round(((rect.top + rect.height / 2 - hallRect.top) / hallRect.height) * 100);
            const alpha    = (0.04 + this.intensity * 0.07).toFixed(3);
            this.cssLight.style.background =
              `radial-gradient(ellipse 54% 62% at 68% ${yPct}%, rgba(200,184,154,${alpha}) 0%, transparent 72%)`;
          }
        });

        link.addEventListener('mouseleave', () => {
          if (this.isNavigating) return;
          this.el.removeAttribute('data-hovered');
          if (this.atm)             { this.atm.setMode('idle'); }
          else if (this.cssLight)   { this.cssLight.style.background = ''; }
        });
      });
    }

    _bindLinks() {
      this.links.forEach(link => {
        link.addEventListener('click', e => {
          if (this.isNavigating) {
            e.preventDefault();
            return;
          }
          const href = link.getAttribute('href');
          if (!href || href === '#') return;
          e.preventDefault();
          this._setSelectedState(link);
          this.audio?.playSFX('navigate');
          this._navigate(href);
        });
      });
    }

    _setSelectedState(link) {
      this.isNavigating = true;
      this.el.classList.add('is-transitioning');
      this.el.removeAttribute('data-hovered');

      this.links.forEach(l => l.classList.remove('is-selected'));
      this.items.forEach(i => i.classList.remove('is-selected'));
      link.classList.add('is-selected');

      const item = link.closest('.nox-drop-item');
      if (item) item.classList.add('is-selected');

      const hallRect = this.el.getBoundingClientRect();
      const rect = link.getBoundingClientRect();
      const xPct = Math.max(0, Math.min(100, ((rect.left + rect.width / 2 - hallRect.left) / hallRect.width) * 100));
      const yPct = Math.max(0, Math.min(100, ((rect.top + rect.height / 2 - hallRect.top) / hallRect.height) * 100));
      this.el.style.setProperty('--pick-x', `${xPct.toFixed(1)}%`);
      this.el.style.setProperty('--pick-y', `${yPct.toFixed(1)}%`);

      if (this.atm) {
        this.atm.setFocus(xPct / 100, yPct / 100);
        this.atm.setMode('active');
      } else if (this.cssLight) {
        const alpha = (0.06 + this.intensity * 0.09).toFixed(3);
        this.cssLight.style.background =
          `radial-gradient(ellipse 56% 66% at ${xPct.toFixed(1)}% ${yPct.toFixed(1)}%, rgba(200,184,154,${alpha}) 0%, transparent 72%)`;
        this.cssLight.style.opacity = '1';
      }
    }

    _navigate(href) {
      if (window.NoxPageTransition?.navigate) {
        window.NoxPageTransition.navigate(href, {
          source: this.el,
          duration: this.transition.duration,
          intensity: this.transition.intensity,
          chaos: this.transition.chaos,
          blur: this.transition.blur
        });
        return;
      }
      window.location.href = href;
    }

    _bindAudioBtn() {
      if (!this.audioBtn || !this.audio) return;
      const iconOn  = this.audioBtn.querySelector('.nox-audio-icon--on');
      const iconOff = this.audioBtn.querySelector('.nox-audio-icon--off');
      this.audioBtn.addEventListener('click', () => {
        const muted = this.audio.toggle();
        this.audioBtn.setAttribute('aria-pressed', String(muted));
        if (iconOn)  iconOn.style.display  = muted ? 'none' : '';
        if (iconOff) iconOff.style.display = muted ? '' : 'none';
      });
    }

    destroy() {
      if (this.atm) {
        this.atm.destroy();
        this.atm = null;
      }
    }
  }

  let activeHall = null;
  function mount(el) {
    if (activeHall) activeHall.destroy();
    activeHall = new Hall(el);
  }

  function init() {
    const el = document.querySelector('.nox-hall');
    if (el) mount(el);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', e => {
    const el = e.target.querySelector('.nox-hall');
    if (el) mount(el);
  });
  document.addEventListener('shopify:section:unload', e => {
    if (!activeHall) return;
    const el = e.target.querySelector('.nox-hall');
    if (el && activeHall.el === el) {
      activeHall.destroy();
      activeHall = null;
    }
  });
})();
